import {
  buildExpandedAmountClause,
  fmtPctDisplay,
  fmtUsdAmount,
  fmtUsdDisplay,
  formatTermSheetUsDate,
  hasExpandedAmount,
  pctDisplayNumber,
} from './termSheetFormatters';
import {
  computeTotalCohortInvestment,
} from './termSheetPayload';
import {
  buildCohortCapLine,
  buildMaxCacClause,
  buildMinInvestmentLine,
  buildMonthlyCohortLine,
  buildPctOfFundingClause,
  hasMaxCac,
  hasRecentCohortCap,
} from './buildInvestmentSizeSection';
import {
  buildFundingFeeClause,
  buildFundingFeePayablePhrase,
  FUNDING_FEE_TYPE_PCT,
  FUNDING_FEE_TYPE_USD,
  inferFundingFeeType,
} from './buildFundingFeeSection';
import {
  buildNonUtilizationFeeClause,
  buildWarrantsClause,
} from './buildOptionalSections';
import { buildSecurityClause } from './buildRepaymentSection';
import { buildConfidentialityClause } from './buildConfidentialitySection';
import { buildCappedReturnClause } from './buildCappedReturnSection';
import { buildNoShopClause, formatNoShopDays } from './buildNoShopSection';
import { buildCompanyClause } from './buildCompanySection';
import { buildCostsAndExpensesClause } from './buildCostsAndExpensesSection';
import { buildRoasCurveClause, buildRoasTablePreview, buildRepaymentPreviewSummary, buildRepaymentSummary } from './buildRoasTablePreview';
import { previewInput } from './previewInput';

function fmtUsd(value) {
  return fmtUsdDisplay(value);
}

function fmtNum(value) {
  if (value === '' || value == null) {
    return null;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return null;
  }
  return String(n);
}

function fmtPct(value) {
  return fmtPctDisplay(value);
}

function fieldOrPlaceholder(value, formatter = (v) => String(v)) {
  if (value === '' || value == null) {
    return '[]';
  }
  const formatted = formatter(value);
  if (formatted == null || formatted === '') {
    return '[]';
  }
  return formatted;
}

function pctFieldOrPlaceholder(value) {
  const pct = pctDisplayNumber(value);
  return pct == null ? '[]' : pct;
}

function usdFieldOrPlaceholder(value) {
  const amount = fmtUsdAmount(value);
  return amount == null ? '[]' : amount;
}

function placeholder(text = '[]') {
  return { text, isPlaceholder: true };
}

function filled(text) {
  return { text, isPlaceholder: false };
}

function valueOrPlaceholder(value, formatter = (v) => String(v)) {
  if (value === '' || value == null) {
    return placeholder();
  }
  const formatted = formatter(value);
  if (formatted == null || formatted === '') {
    return placeholder();
  }
  return filled(formatted);
}

function buildFundingFeePayable(fields) {
  return filled(buildFundingFeePayablePhrase(fields));
}

function buildRoasCurveOption(fields) {
  return filled(buildRoasCurveClause(fields));
}

export function buildTermSheetClauses(fields) {
  const totalCohortInvestment = computeTotalCohortInvestment(fields);
  const roasTable = buildRoasTablePreview(fields);

  return {
    company: {
      summary: buildCompanyClause(fields),
    },
    investmentSize: {
      maxOutstanding: valueOrPlaceholder(fields.maxOutstandingInvestment, fmtUsd),
      expandedAmount: valueOrPlaceholder(fields.expandedAmount, fmtUsd),
      monthlyCohorts: valueOrPlaceholder(fields.monthlyCohorts, fmtNum),
      maxPerCohort: valueOrPlaceholder(fields.maxAmountPerCohort, fmtUsd),
      pctOfFunding: valueOrPlaceholder(fields.pctOfFunding, fmtPct),
      totalCohortInvestment:
        totalCohortInvestment != null
          ? filled(fmtUsd(totalCohortInvestment))
          : placeholder('[[]]'),
      maxPctIncrease: valueOrPlaceholder(fields.maxPctIncreaseFromLast, fmtPct),
      maxAmountIncrease: valueOrPlaceholder(fields.maxAmountIncreaseFromLast, fmtUsd),
      minInvestment: valueOrPlaceholder(fields.minInvestment, fmtUsd),
      maxCac: valueOrPlaceholder(fields.maxCac, fmtUsd),
      summary: buildInvestmentSizeSummary(fields, totalCohortInvestment),
    },
    term: {
      months: valueOrPlaceholder(fields.termMonths, fmtNum),
      cohorts: valueOrPlaceholder(fields.termCohorts, fmtNum),
      summary: buildTermSummary(fields),
    },
    repayment: {
      roasCurveOption: buildRoasCurveOption(fields),
      roasTable,
      summary: buildRepaymentSummary(fields),
    },
    security: {
      summary: buildSecurityClause(fields),
    },
    cappedReturn: {
      summary: buildCappedReturnClause(fields),
    },
    confidentiality: {
      summary: buildConfidentialityClause(fields),
    },
    noShop: {
      summary: buildNoShopClause(fields),
    },
    fundingFee: {
      usd: valueOrPlaceholder(fields.fundingFeeUsd, fmtUsd),
      pct: valueOrPlaceholder(fields.fundingFeePct, fmtPct),
      payable: buildFundingFeePayable(fields),
      summary: buildFundingFeeSummary(fields),
    },
    minimumLiquidity: {
      months: valueOrPlaceholder(fields.minLiquidityMonths, fmtNum),
      usd: valueOrPlaceholder(fields.minLiquidityUsd, fmtUsd),
      summary: buildMinimumLiquiditySummary(fields),
    },
    nonUtilizationFee: {
      enabled: Boolean(fields.nonUtilizationFeeEnabled),
      summary: buildNonUtilizationFeeSummary(fields),
    },
    warrants: {
      enabled: Boolean(fields.warrantsEnabled),
      summary: buildWarrantsSummary(fields),
    },
    costsAndExpenses: {
      summary: buildCostsAndExpensesClause(fields),
    },
  };
}

function bulletLine(text) {
  return text ? `• ${text}` : '';
}

function buildInvestmentSizeSummary(fields, totalCohortInvestment) {
  const maxOutstanding = usdFieldOrPlaceholder(fields.maxOutstandingInvestment);
  const expandedClause = buildExpandedAmountClause(fields.expandedAmount);

  return [
    `US $${maxOutstanding} max outstanding investment size${expandedClause}`,
    'Monthly investment limits as will be more fully documented, and which will include:',
    bulletLine(
      buildMonthlyCohortLine(
        fields,
        (value) => fieldOrPlaceholder(value, fmtNum),
        (value) => usdFieldOrPlaceholder(value),
        totalCohortInvestment
      )
    ),
    bulletLine(buildPctOfFundingClause(fields)),
    bulletLine(
      buildCohortCapLine(fields, pctFieldOrPlaceholder, usdFieldOrPlaceholder)
    ),
    bulletLine(
      buildMinInvestmentLine(fields, (value) => usdFieldOrPlaceholder(value))
    ),
    buildMaxCacClause(fields, (value) => usdFieldOrPlaceholder(value)),
  ]
    .filter(Boolean)
    .join('\n\n');
}

function buildTermSummary(fields) {
  const months = fieldOrPlaceholder(fields.termMonths, fmtNum);
  const cohorts = fieldOrPlaceholder(fields.termCohorts, fmtNum);
  return [
    `Investment period: ${months} months (allowing ${cohorts} cohort investments)`,
    'Investment return wind down period: as needed pursuant to ROAS',
    'Investment period could be extended upon mutual consent',
  ].join('\n\n');
}

function buildFundingFeeSummary(fields) {
  return buildFundingFeeClause(fields, usdFieldOrPlaceholder, pctFieldOrPlaceholder);
}

function buildMinimumLiquiditySummary(fields) {
  const months = fieldOrPlaceholder(fields.minLiquidityMonths, fmtNum);
  const usd = usdFieldOrPlaceholder(fields.minLiquidityUsd);
  return `Minimum cash balance equal to the greater of (i) ${months} months of operating expenses (excluding advertising spend) and (ii) US $${usd}, to be maintained at all times`;
}

function buildNonUtilizationFeeSummary(fields) {
  if (!fields.nonUtilizationFeeEnabled) {
    return '';
  }
  const wording = buildNonUtilizationFeeClause(fields);
  return wording || '[]';
}

function buildWarrantsSummary(fields) {
  if (!fields.warrantsEnabled) {
    return '';
  }
  const wording = buildWarrantsClause(fields);
  return wording || '[]';
}

function buildExpandedAmountPreviewClause(value) {
  if (!hasExpandedAmount(value)) {
    return '. ';
  }
  return `, with an option to expand to US ${previewInput(fmtUsdDisplay(value))} upon mutual consent. `;
}

function buildPctOfFundingPreviewClause(fields) {
  const pct = previewInput(pctFieldOrPlaceholder(fields.pctOfFunding));
  const base = `Up to ${pct}% of the associated cohort marketing spend per cohort`;
  if (hasRecentCohortCap(fields)) {
    return base;
  }
  return `${base}, subject to the Company’s monthly marketing plan`;
}

function buildMonthlyCohortPreviewLine(fields, totalCohortInvestment) {
  const cohorts = previewInput(fieldOrPlaceholder(fields.monthlyCohorts, fmtNum));
  const perCohort = previewInput(usdFieldOrPlaceholder(fields.maxAmountPerCohort));
  const total =
    totalCohortInvestment != null
      ? previewInput(usdFieldOrPlaceholder(totalCohortInvestment))
      : previewInput('[[]]');
  return `${cohorts} monthly cohort investments of up to US $${perCohort} each (a total cohort investment amount of up to US $${total})`;
}

function buildMaxCacPreviewClause(fields) {
  if (hasMaxCac(fields)) {
    return `Each investment will be subject, among other things, to a customer acquisition cost for the applicable cohort being no more than US $${previewInput(usdFieldOrPlaceholder(fields.maxCac))}`;
  }
  return buildMaxCacClause(fields, () => 'an amount to be determined in the definitive agreement');
}

function buildInvestmentSizePreviewSummary(fields, totalCohortInvestment) {
  const maxOutstanding = previewInput(usdFieldOrPlaceholder(fields.maxOutstandingInvestment));
  const expandedClause = buildExpandedAmountPreviewClause(fields.expandedAmount);

  return [
    `US $${maxOutstanding} max outstanding investment size${expandedClause}`,
    'Monthly investment limits as will be more fully documented, and which will include:',
    bulletLine(buildMonthlyCohortPreviewLine(fields, totalCohortInvestment)),
    bulletLine(buildPctOfFundingPreviewClause(fields)),
    bulletLine(
      buildCohortCapLine(fields, (value) => previewInput(pctFieldOrPlaceholder(value)), (value) =>
        previewInput(usdFieldOrPlaceholder(value))
      )
    ),
    bulletLine(
      buildMinInvestmentLine(fields, (value) => previewInput(usdFieldOrPlaceholder(value)))
    ),
    buildMaxCacPreviewClause(fields),
  ]
    .filter(Boolean)
    .join('\n\n');
}

function buildTermPreviewSummary(fields) {
  const months = previewInput(fieldOrPlaceholder(fields.termMonths, fmtNum));
  const cohorts = previewInput(fieldOrPlaceholder(fields.termCohorts, fmtNum));
  return [
    `Investment period: ${months} months (allowing ${cohorts} cohort investments)`,
    'Investment return wind down period: as needed pursuant to ROAS',
    'Investment period could be extended upon mutual consent',
  ].join('\n\n');
}

function buildFundingFeePayablePreviewPhrase(fields) {
  const installments = Number(fields.fundingFeeInstallments);
  if (!Number.isFinite(installments) || installments <= 0) {
    return `payable in equal instalments across ${previewInput('[]')} months`;
  }
  const label = installments === 1 ? 'month' : 'months';
  return `payable in equal instalments across ${previewInput(String(installments))} ${label}`;
}

function buildFundingFeePreviewSummary(fields) {
  const payable = buildFundingFeePayablePreviewPhrase(fields);
  const feeType = inferFundingFeeType(fields);

  if (feeType === FUNDING_FEE_TYPE_USD) {
    return `One time fee of ${previewInput(`US $${usdFieldOrPlaceholder(fields.fundingFeeUsd)}`)}, ${payable}`;
  }
  if (feeType === FUNDING_FEE_TYPE_PCT) {
    return `One time fee of ${previewInput(`${pctFieldOrPlaceholder(fields.fundingFeePct)}% of the total investment size amount`)}, ${payable}`;
  }

  return `One time fee of ${previewInput(`US $${usdFieldOrPlaceholder('')}`)} [OR] ${previewInput(`${pctFieldOrPlaceholder('')}% of the total investment size amount`)}, ${payable}`;
}

function buildMinimumLiquidityPreviewSummary(fields) {
  const months = previewInput(fieldOrPlaceholder(fields.minLiquidityMonths, fmtNum));
  const usd = previewInput(usdFieldOrPlaceholder(fields.minLiquidityUsd));
  return `Minimum cash balance equal to the greater of (i) ${months} months of operating expenses (excluding advertising spend) and (ii) US $${usd}, to be maintained at all times`;
}

function buildConfidentialityPreviewSummary(fields) {
  if (!fields.confidentialityHasNda) {
    return buildConfidentialityClause(fields);
  }
  const date = previewInput(formatTermSheetUsDate(fields.confidentialityNdaDate));
  return `The parties acknowledge and agree that the exchange of information and all discussions relating to this Term Sheet and the proposed transaction are subject to the terms of the non-disclosure agreement dated ${date} between the parties, which shall continue in full force and effect in accordance with its terms.`;
}

function buildNoShopPreviewSummary(fields) {
  const period = previewInput(formatNoShopDays(fields.noShopDays));
  return buildNoShopClause(fields).replace(formatNoShopDays(fields.noShopDays), period);
}

function buildCappedReturnPreviewSummary(fields) {
  return buildCappedReturnClause(fields, (value) =>
    previewInput(pctFieldOrPlaceholder(value))
  );
}

function buildCompanyPreviewSummary(fields) {
  const companyName = previewInput(
    String(fields.companyName ?? '').trim() || '[Company Name]'
  );
  const jurisdiction = previewInput(
    String(fields.companyJurisdiction ?? '').trim() || '[Jurisdiction]'
  );
  const fileNumber = previewInput(
    String(fields.companyFileNumber ?? '').trim() || '[file number]'
  );
  return `${companyName} (the "Company") incorporated in ${jurisdiction} with file number ${fileNumber}. [Note to Company: please also advise of any jurisdiction and foreign entity file numbers in the jurisdiction of your HQ or other material business operations]`;
}

function buildCostsAndExpensesPreviewSummary(fields) {
  return buildCostsAndExpensesClause(fields, (value) => previewInput(fmtUsdAmount(value)));
}

export function buildTermSheetPreviewParagraphs(fields) {
  const clauses = buildTermSheetClauses(fields);
  const totalCohortInvestment = computeTotalCohortInvestment(fields);
  const paragraphs = [
    { title: 'Company', text: buildCompanyPreviewSummary(fields) },
    { title: 'Investment Size', text: buildInvestmentSizePreviewSummary(fields, totalCohortInvestment) },
    { title: 'Term', text: buildTermPreviewSummary(fields) },
    { title: 'Repayment Mechanism', text: buildRepaymentPreviewSummary(fields) },
    { title: 'Security', text: previewInput(buildSecurityClause(fields)) },
    { title: 'Capped Return', text: buildCappedReturnPreviewSummary(fields) },
    { title: 'Funding Fee', text: buildFundingFeePreviewSummary(fields) },
    { title: 'Minimum Liquidity', text: buildMinimumLiquidityPreviewSummary(fields) },
  ];
  if (clauses.nonUtilizationFee.enabled) {
    paragraphs.push({
      title: 'Non-Utilization Fee',
      text: previewInput(clauses.nonUtilizationFee.summary || '[]'),
    });
  }
  if (clauses.warrants.enabled) {
    paragraphs.push({
      title: 'Warrants',
      text: previewInput(clauses.warrants.summary || '[]'),
    });
  }
  paragraphs.push({
    title: 'Confidentiality',
    text: buildConfidentialityPreviewSummary(fields),
  });
  paragraphs.push({
    title: 'No Shop',
    text: buildNoShopPreviewSummary(fields),
  });
  paragraphs.push({
    title: 'Costs and Expenses',
    text: buildCostsAndExpensesPreviewSummary(fields),
  });
  return paragraphs;
}
